import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface AuditDto { auditRecId: number; employeeId: number | null; userName: string | null;
  eventType: string; eventDescription: string; createdOn: string; ipAddress: string | null; browserDesc: string | null; }
interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

export function UserAuditPage() {
  const [page, setPage] = useState(0);
  const [eventType, setEventType] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [open, setOpen] = useState<AuditDto | null>(null);

  const params = new URLSearchParams({ page: String(page), pageSize: "50" });
  if (eventType) params.set("eventType", eventType);
  if (q) params.set("q", q);
  if (from) params.set("from", new Date(from).toISOString());
  if (to) params.set("to", new Date(to).toISOString());

  const { data, isLoading } = useQuery({
    queryKey: ["user-audit", params.toString()],
    queryFn: () => apiFetch<Paged<AuditDto>>(`/api/user-audit?${params.toString()}`),
  });

  const cols: DataGridColumn<AuditDto>[] = [
    { id: "ts",   header: "Timestamp", accessor: (r) => new Date(r.createdOn).toISOString() },
    { id: "user", header: "User", accessor: (r) => r.userName ?? `#${r.employeeId ?? "?"}` },
    { id: "type", header: "Event", accessor: (r) => <button className="underline" onClick={() => setOpen(r)}>{r.eventType}</button> },
    { id: "desc", header: "Description", accessor: (r) => r.eventDescription },
    { id: "ip",   header: "IP", accessor: (r) => r.ipAddress ?? "—" },
  ];

  return (
    <ListPage title="User audit" filterBar={
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div><Label htmlFor="et">Event type</Label><Input id="et" value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(0); }} /></div>
        <div><Label htmlFor="q">Search</Label><Input id="q" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} /></div>
        <div><Label htmlFor="from">From (UTC)</Label><Input id="from" type="datetime-local" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /></div>
        <div><Label htmlFor="to">To (UTC)</Label><Input id="to" type="datetime-local" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} /></div>
      </div>
    }>
      <DataGrid rows={data?.items ?? []} columns={cols} rowKey={(r) => r.auditRecId}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={isLoading} onPageChange={setPage} />
      <Dialog open={open !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{open?.eventType}</DialogTitle></DialogHeader>
          <pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs">{open ? JSON.stringify(open, null, 2) : ""}</pre>
          <Button onClick={() => setOpen(null)}>Close</Button>
        </DialogContent>
      </Dialog>
    </ListPage>
  );
}
